import { useCallback, useEffect, useReducer, useRef } from "react";

import { deleteInsightsConversation, streamInsightsChat } from "../../apis/insights";

// Assistant messages are a list of parts in arrival order:
//   { type: "text", text }
//   { type: "tool", id, name, status: "pending" | "running" | "ok" | "error", input, result }
//   { type: "chart", id, spec }

const initialState = { conversationId: null, database: null, messages: [], streaming: false };

let nextId = 0;
const newId = () => `m${++nextId}`;

function updateLastAssistant(state, update) {
	const messages = [...state.messages];
	const index = messages.length - 1;
	if (index < 0 || messages[index].role !== "assistant") {
		return state;
	}
	messages[index] = update(messages[index]);
	return { ...state, messages };
}

function updatePart(message, id, update) {
	return { ...message, parts: message.parts.map((part) => (part.id === id ? update(part) : part)) };
}

function reducer(state, action) {
	switch (action.type) {
		case "send":
			return {
				...state,
				streaming: true,
				messages: [
					...state.messages,
					{ id: newId(), role: "user", text: action.text },
					{ id: newId(), role: "assistant", parts: [], thinking: "", status: "streaming" },
				],
			};
		case "conversation":
			return { ...state, conversationId: action.id, database: action.database };
		case "thinking":
			return updateLastAssistant(state, (m) => ({ ...m, thinking: m.thinking + action.delta }));
		case "text":
			return updateLastAssistant(state, (m) => {
				const parts = [...m.parts];
				const last = parts[parts.length - 1];
				if (last?.type === "text") {
					parts[parts.length - 1] = { ...last, text: last.text + action.delta };
				} else {
					parts.push({ type: "text", id: newId(), text: action.delta });
				}
				return { ...m, parts };
			});
		case "tool_pending":
			return updateLastAssistant(state, (m) =>
				m.parts.some((p) => p.id === action.id)
					? m
					: { ...m, parts: [...m.parts, { type: "tool", id: action.id, name: action.name, status: "pending" }] },
			);
		case "tool_start":
			return updateLastAssistant(state, (m) => updatePart(m, action.id, (p) => ({ ...p, status: "running", input: action.input })));
		case "tool_result":
			return updateLastAssistant(state, (m) =>
				updatePart(m, action.id, (p) => ({ ...p, status: action.ok ? "ok" : "error", result: action })),
			);
		case "chart":
			return updateLastAssistant(state, (m) => ({ ...m, parts: [...m.parts, { type: "chart", id: `chart-${action.id}`, spec: action.spec }] }));
		case "finish":
			return {
				...updateLastAssistant(state, (m) => ({
					...m,
					status: action.error ? "error" : action.stopped ? "stopped" : "done",
					error: action.error,
					errorCode: action.errorCode,
					// Anything still marked as running did not finish.
					parts: m.parts.map((p) => (p.type === "tool" && (p.status === "pending" || p.status === "running") ? { ...p, status: "error" } : p)),
				})),
				streaming: false,
			};
		case "reset":
			return { ...initialState };
		default:
			return state;
	}
}

export function useInsightsChat() {
	const [state, dispatch] = useReducer(reducer, initialState);
	const controllerRef = useRef(null);
	const stateRef = useRef(state);
	stateRef.current = state;

	useEffect(() => () => controllerRef.current?.abort(), []);

	const send = useCallback(async (text, database) => {
		const question = text.trim();
		if (!question || stateRef.current.streaming) {
			return;
		}
		const controller = new AbortController();
		controllerRef.current = controller;
		dispatch({ type: "send", text: question });

		let failed = null;
		try {
			await streamInsightsChat({
				message: question,
				conversationId: stateRef.current.conversationId,
				database: stateRef.current.database || database,
				signal: controller.signal,
				onEvent: (event, data) => {
					if (event === "conversation") {
						dispatch({ type: "conversation", id: data.id, database: data.database });
					} else if (event === "error") {
						failed = { message: data.message };
					} else if (["thinking", "text", "tool_pending", "tool_start", "tool_result", "chart"].includes(event)) {
						dispatch({ type: event, ...data });
					}
				},
			});
		} catch (error) {
			if (!controller.signal.aborted) {
				failed = { message: error.message || "Could not reach Outfit Insights.", code: error.code };
			}
		} finally {
			controllerRef.current = null;
			dispatch({ type: "finish", error: failed?.message, errorCode: failed?.code, stopped: controller.signal.aborted });
		}
	}, []);

	const stop = useCallback(() => controllerRef.current?.abort(), []);

	const reset = useCallback(() => {
		controllerRef.current?.abort();
		if (stateRef.current.conversationId) {
			deleteInsightsConversation(stateRef.current.conversationId);
		}
		dispatch({ type: "reset" });
	}, []);

	return { ...state, send, stop, reset };
}
